import {
  ZfDivider,
  ZfFormField,
  ZfFormFieldAlert,
  ZfFormFieldLabel,
  ZfInput,
  ZfLoadingSpinner,
  ZfSelectOption,
  ZfStack,
  ZfTypography,
} from '@zafire/react';
import { CashAccount } from './AddPortfolioDialog';
import { useFormikContext } from 'formik';
import { useGetDropdown } from './hooks/useGetDropdown';
import useGetSequenceNumber from './hooks/useGetSequenceNumber';
import useGetBeneficialOwners from './hooks/useGetBeneficialOwners';
import { SequenceNumberForBrStatusEnum } from 'pco-product-service-internal-consumer';
import { RowComponentProps } from 'react-window';
import { useMemo } from 'react';
import { EditPortfolioForm } from './EditPortfolioDialog';

export type RowProps = RowComponentProps & {
  item: CashAccount;
};

type EditPortfolioDetailSectionType = { brUid: string; canEditDetails: boolean };

const EditPortfolioDetailSection = ({ brUid, canEditDetails }: EditPortfolioDetailSectionType) => {
  const { values, touched, errors, setFieldValue, getFieldProps } = useFormikContext<EditPortfolioForm>();
  const { currencyData, portfolioTypeData } = useGetDropdown();

  const { data: sequenceNumberList, isLoading: sequenceNumberLoading } = useGetSequenceNumber(
    brUid,
    values.portfolioType,
  );

  const sequenceList = useMemo(() => {
    if (!sequenceNumberLoading && sequenceNumberList) {
      return sequenceNumberList?.sequenceNumbers || [];
    }

    return [];
  }, [sequenceNumberList, sequenceNumberLoading]);

  const { data: beneficialData } = useGetBeneficialOwners(brUid || '', brUid !== '');

  return (
    <ZfStack spacing="medium">
      <ZfStack direction="row" spacing="small">
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          Portfolio configuration
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
      </ZfStack>
      <ZfStack direction="row" spacing="small">
        <ZfFormField disabled style={{ flex: 1 }}>
          <ZfFormFieldLabel slot="label" htmlFor="referenceCurrency" text="Reference currency" mandatory />

          <ZfInput
            disabled
            name="referenceCurrency"
            type="select"
            onSelectChange={(e) => {
              setFieldValue('referenceCurrency', e.detail);
            }}
            id="referenceCurrency"
            error={!!(touched.referenceCurrency && errors.referenceCurrency)}
            allowSearch
            value={values.referenceCurrency}
            getOptionLabel={(option) => currencyData?.find((co) => co.value.value === option)?.value?.label || option}
          >
            {currencyData?.map((co) => {
              return (
                <ZfSelectOption
                  key={co.key}
                  value={co.value.value}
                  isSelected={values.referenceCurrency === co.value.value}
                >
                  {co.value.label}
                </ZfSelectOption>
              );
            })}
          </ZfInput>
          <ZfFormFieldAlert slot="alert" id="referenceCurrency" alerts={[errors.referenceCurrency?.toString() ?? '']} />
        </ZfFormField>

        <ZfFormField style={{ flex: 1 }} disabled>
          <ZfFormFieldLabel slot="label" htmlFor="portfolioType" text="Portfolio type" mandatory />

          <ZfInput
            name="portfolioType"
            type="select"
            onSelectChange={(e) => {
              setFieldValue('portfolioType', e.detail);
              const portfolioTypeNumber = portfolioTypeData?.find((item) => item.value.value === e.detail)?.value.code;
              setFieldValue('portfolioTypeNumber', portfolioTypeNumber);
            }}
            disabled
            allowSearch
            error={!!errors.portfolioType && touched.portfolioType}
            id="portfolioType"
            value={values.portfolioType}
            getOptionLabel={(option) =>
              portfolioTypeData?.find((co) => co.value.value === option)?.value?.label || option
            }
          >
            {portfolioTypeData?.map((co) => {
              return (
                <ZfSelectOption key={co.key} value={co.key} isSelected={values.referenceCurrency === co.key}>
                  {co.value.label}
                </ZfSelectOption>
              );
            })}
          </ZfInput>
          <ZfFormFieldAlert slot="alert" id="portfolioType" alerts={[errors.portfolioType?.toString() ?? '']} />
        </ZfFormField>

        <ZfFormField style={{ flex: 1 }} disabled>
          <ZfFormFieldLabel slot="label" htmlFor="sequenceNumber" text="Sequence number" mandatory />

          <ZfInput
            name="sequenceNumber"
            type="select"
            onSelectChange={(e) => {
              setFieldValue('sequenceNumber', e.detail);
            }}
            allowSearch
            error={!!errors.sequenceNumber && touched.sequenceNumber}
            id="sequenceNumber"
            value={values.sequenceNumber}
            disabled
            getOptionLabel={(option) =>
              sequenceList.find((co) => co.sequenceNumber === option)?.sequenceNumber || option
            }
          >
            {sequenceNumberLoading && (
              <ZfLoadingSpinner slot="trailing" size="extra-small" color="primary" type="infinite"></ZfLoadingSpinner>
            )}
            {!sequenceNumberLoading &&
              sequenceList.map((co) => {
                return (
                  <ZfSelectOption
                    disabled={co.status === SequenceNumberForBrStatusEnum.Inactive}
                    key={co.sequenceNumber}
                    value={co.sequenceNumber}
                    isSelected={
                      co.sequenceNumber !== undefined &&
                      values.portfolioTypeNumber?.slice(-2).includes(co.sequenceNumber)
                    }
                  >
                    {co.sequenceNumber}
                  </ZfSelectOption>
                );
              })}
          </ZfInput>
          <ZfFormFieldAlert slot="alert" id="sequenceNumber" alerts={[errors.sequenceNumber ?? '']} />
        </ZfFormField>
      </ZfStack>

      <ZfStack direction="row" spacing="small">
        <ZfFormField disabled style={{ flex: 1 }}>
          <ZfFormFieldLabel slot="label" htmlFor="chargingCurrency" text="Charging currency" mandatory />

          <ZfInput
            disabled
            name="chargingCurrency"
            type="select"
            onSelectChange={(e) => {
              setFieldValue('chargingCurrency', e.detail);
            }}
            id="chargingCurrency"
            error={!!(touched.chargingCurrency && errors.chargingCurrency)}
            allowSearch
            value={values.chargingCurrency}
            getOptionLabel={(option) => currencyData?.find((co) => co.value.value === option)?.value?.label || option}
          >
            {currencyData?.map((co) => {
              return (
                <ZfSelectOption
                  key={co.key}
                  value={co.value.value}
                  isSelected={values.chargingCurrency === co.value.value}
                >
                  {co.value.label}
                </ZfSelectOption>
              );
            })}
          </ZfInput>
          <ZfFormFieldAlert slot="alert" id="chargingCurrency" alerts={[errors.chargingCurrency?.toString() ?? '']} />
        </ZfFormField>

        <ZfFormField style={{ flex: 1 }} label="Rubric" alerts={[errors.rubrik ?? '']}>
          <ZfFormFieldLabel slot="label" htmlFor="chargingCurrency" text="Rubric" />

          <ZfInput
            name="rubrik"
            onInputChange={getFieldProps('rubrik').onChange}
            allowSearch
            error={!!errors.rubrik && touched.rubrik}
            id="rubrik"
            value={values.rubrik}
          ></ZfInput>
        </ZfFormField>
        <ZfFormFieldAlert slot="alert" id="rubrik" alerts={[errors.rubrik?.toString() ?? '']} />

        <ZfFormField style={{ flex: 1 }} disabled>
          <ZfFormFieldLabel slot="label" htmlFor="allocatedBo" text="Special beneficial owner (100% quota)" />
          <ZfInput
            name="allocatedBo"
            type="select"
            onSelectChange={(e) => {
              setFieldValue('allocatedBo', e.detail);
            }}
            allowSearch
            error={!!errors.allocatedBo && touched.allocatedBo}
            id="allocatedBo"
            value={values.allocatedBo}
            disabled
            getOptionLabel={(option) => {
              const data = beneficialData?.find((co) => co.partnerUid === option);
              if (data) {
                return `${data.partnerName} ${data.partnerId} ${data.partnerToBrRole}`;
              }
              return option;
            }}
          >
            {beneficialData?.map((co) => {
              return (
                <ZfSelectOption
                  key={co.partnerUid}
                  value={co.partnerUid}
                  isSelected={values.allocatedBo === co.partnerUid}
                >
                  {co.partnerName} {co.partnerId} {co.partnerToBrRole}
                </ZfSelectOption>
              );
            })}
          </ZfInput>
          <ZfFormFieldAlert slot="alert" id="allocatedBo" alerts={[errors.allocatedBo ?? '']} />
        </ZfFormField>
      </ZfStack>
      <ZfStack direction="row" spacing="small">
        <ZfFormField style={{ flex: 1 }} disabled={!canEditDetails}>
          <ZfFormFieldLabel slot="label" htmlFor="details" text="Detail" />

          <ZfInput
            disabled={!canEditDetails}
            maxLength={1000}
            name="details"
            onInputChange={getFieldProps('details').onChange}
            error={!!errors.details && touched.details}
            id="details"
            value={values.details}
          ></ZfInput>
          <ZfFormFieldAlert slot="alert" id="details" alerts={[errors.details ?? '']} />
        </ZfFormField>
        <div style={{ flex: 2 }} />
      </ZfStack>
    </ZfStack>
  );
};

export default EditPortfolioDetailSection;
